import React, { useCallback, useEffect, useState } from 'react';

import { Box, Flex, NumberInput, NumberInputField, NumberInputStepper, useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import SkeletonLoader from 'components/SkeletonLoader';
import TextParseComponent from 'components/feed/TextParseComponent';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import {
  useReviewPreviewPerformanceGoalsCascadedViewData,
  goalsReviewCascadedViewReactQueryKey,
} from 'screens/performanceReview/LandingPage/reviewPreviewPerformance/hooks/dataHooks';
import { useInvalidateGoalFormData } from 'screens/performanceReview/components/GoalForm/hooks/dataHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { StyledBox, StyledTypography } from 'styles/common';

import {
  ArrowRight,
  KeyResultIcon,
  KeyResultPrivate2,
  ObjectiveIcon,
  PrivateObjective,
} from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { roundOffToPlaces } from 'constants/helper';
import { sendChangeWeightageEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { getEmployeeDetails } from 'services/employee';
import { updateReviewGoalsWeightage } from 'services/performanceReview';
import { getReviewGoalsTimelineList } from 'services/reviews';
import { showErrorSnackbar, showInfoSnackbar } from 'services/snackbar';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { Drawer } from 'uiToolkitV1/Drawer';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { ProgressBar } from 'uiToolkitV1/ProgressBar';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium, TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

import { useGoalsWeight } from './GoalsWeightContext';

const MAX_WEIGHTAGE = 100;
/* keeping decimal precision for percentages to 10 decimal places */
const PRECISION = 10;

const EmpInfo = ({ employeeDetails }) => (
  <Box px={7} py={3}>
    <ListItemComp
      // eslint-disable-next-line react/no-unstable-nested-components
      LeftComponent={() => <Avatar src={employeeDetails?.profilePhotoSrc} />}
      title={employeeDetails.fullName + (employeeDetails.designation ? `, ${employeeDetails.designation}` : '')}
      subTitle={employeeDetails.email}
      withoutBackground
    />
  </Box>
);

export const renderParentIcon = (type) => {
  if (type === 'OBJECTIVE') {
    return <ObjectiveIcon color="icon.g2" boxSize="14px" mr={2} />;
  }
  return <KeyResultIcon color="icon.g2" boxSize="14px" mr={2} />;
};

// export const ReviewCycleOption = ({ children, ...props }) => {
//   return (
//     <components.Option {...props}>
//       <TextBodyMedium bold color="text.g1" w="100%">
//         {children}
//       </TextBodyMedium>
//     </components.Option>
//   );
// };

const Header = ({ employeeDetails, goalsTimelineList = [], currentReviewCycleIndex, setCurrentReviewCycleIndex }) => {
  const { moduleName, keyResultsName } = useOrganisationSettings();
  const reviewCycleOptions = goalsTimelineList.map(({ name }, index) => {
    if (name === 'Current Status') {
      return {
        value: index,
        label: `Currently active ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
      };
    }
    return {
      value: index,
      label: `${name}`,
    };
  });

  const reviewCycleChangeHandler = (selectedOption) => {
    setCurrentReviewCycleIndex(selectedOption.value);
  };

  return (
    <>
      <Box p=" 24px 32px 8px">
        <Flex align="center">
          <TextLabel color="text.g2">Performance Preview</TextLabel>
          <ArrowRight boxSize="20px" color="icon.g2" mx="-2px" />
          <TextLabel bold>{modifyString(moduleName, StringModifier.TITLE_CASE)} weightage</TextLabel>
        </Flex>
      </Box>
      {employeeDetails && <EmpInfo employeeDetails={employeeDetails} />}
      <Flex px={7} justifyContent="flex-start" alignItems="center">
        <TextLabel mr={3}>Review Cycle: </TextLabel>
        <div>
          <SelectDashed
            value={reviewCycleOptions?.[currentReviewCycleIndex]}
            options={reviewCycleOptions}
            handleChange={reviewCycleChangeHandler}
            // variant="outlined"
            // components={{
            //   Option: ReviewCycleOption
            // }}
          />
        </div>
      </Flex>

      <Flex justify="space-between" borderRadius="4px" bg="neutral.F5F7F9" mt="3" mx="4">
        <TextBodyMedium p="8px 12px">{keyResultsName}</TextBodyMedium>
        <TextBodyMedium p="8px 12px 8px 0px" w="25%">
          Weightage
        </TextBodyMedium>
      </Flex>
    </>
  );
};

const renderIcon = ({ type, visibility }) => {
  if (type === 'OBJECTIVE') {
    if (visibility === 'PRIVATE') return <PrivateObjective color="#7651BC" boxSize="24px" mr="8px" />;
    return <Objective color="#7651BC" boxSize="24px" mr="8px" />;
  }
  if (visibility === 'PRIVATE') {
    return <KeyResultPrivate2 boxSize="24px" color="functional.4657AE" mr="10px" />;
  }
  return <KeyResult boxSize="24px" color="functional.4657AE" mr="10px" />;
};

const ProgressStatus = ({ totalWeightage, theme }) => {
  const { keyResultsName } = useOrganisationSettings();

  const isExceeded = totalWeightage > MAX_WEIGHTAGE;

  /* 
    toFixed improves representation e.g. 
    if number is 2.999999999999 and we do toFixed to lesser decimal precision,
    it will show 3.0000000000 as string and when converted to number it turns to 3.
    This avoids the error related to decimal point arithmetic in js. e.g 0.1 + 0.2
  */
  const difference = Math.abs(Number((MAX_WEIGHTAGE - totalWeightage).toFixed(PRECISION)));

  const differenceText = isExceeded ? `${difference}% exceeded the limit` : `${difference}% remaining`;
  const differenceColor = isExceeded ? theme.colors.functional.C62C02 : theme.colors.functional['44812F'];
  return (
    <Flex alignItems="center">
      <Flex flexGrow={1} mr={6} flexDir="column">
        <ProgressBar
          value={totalWeightage}
          color={totalWeightage > 100 ? theme.colors.functional.FD9B9B : theme.colors.functional.DBF2D3}
          size="large"
        />
        <TextBodyMedium bold color={differenceColor} mt={2}>
          {differenceText}
        </TextBodyMedium>
        <TextLabel color="text.g2">
          You cannot save the weightage until you distribute exactly 100% of weightage among your{' '}
          {modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}
        </TextLabel>
      </Flex>
      <TextHeader2
        w="25%"
        bold
        color={totalWeightage !== MAX_WEIGHTAGE ? 'functional.C62C02' : 'functional.44812F'}
        textAlign="end"
      >
        {/* 
          Relying on rounding off to display correct number of digits after decimal.
        */}
        {totalWeightage}%
      </TextHeader2>
    </Flex>
  );
};

const getTotalWeightage = ({ editedGoalsWeight }) => {
  let total = 0;
  editedGoalsWeight.forEach((goal) => {
    total += Number(goal.weightage);
  });
  /* 
    toFixed improves representation e.g. 
    if number is 2.999999999999 and we do toFixed to lesser decimal precision,
    it will show 3.0000000000 as string and when converted to number it turns to 3.
    This avoids the error related to decimal point arithmetic in js. e.g 0.1 + 0.2
  */
  return roundOffToPlaces(total, 2);
};

const GoalsWeightSidepanelV1 = () => {
  const {
    state: { open, canEdit, employeeId, isPreSelectActiveGoals, preSelectedOptionId },
    hideSidePanel,
  } = useGoalsWeight();

  const dispatch = useDispatch();

  const [goalsTimelineList, setGoalsTimelineList] = useState([]);
  const [currentReviewCycleIndex, setCurrentReviewCycleIndex] = useState(null);
  // const [goals, setGoals] = useState([]);
  const [editedGoalsWeight, setEditedGoalsWeight] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const theme = useTheme();
  const totalWeightage = getTotalWeightage({ editedGoalsWeight });

  const currentSelectedOptionAllowEdit = goalsTimelineList?.[currentReviewCycleIndex]?.isOkrChangeWeightageAllowed;

  const { data: goals, isLoading } = useReviewPreviewPerformanceGoalsCascadedViewData({
    startTS: goalsTimelineList?.[currentReviewCycleIndex]?.startTS,
    endTS: goalsTimelineList?.[currentReviewCycleIndex]?.endTS,
    employeeId,
    formId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
  });

  const getGoalIndex = ({ goalId }) => editedGoalsWeight.findIndex((goal) => goal.uuid === goalId);

  // Review query invalidation
  const { employeeId: reviewEmployeeId, providerId, reviewId } = useReviewFormParams();

  const goalFormInvalidateHandler = useInvalidateGoalFormData({
    employeeId: reviewEmployeeId,
    providerId,
    // responseId, // <- will be used to show the submitted
    reviewId,
  });

  // Review Goals form invalidation
  const queryClient = useQueryClient();
  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsReviewCascadedViewReactQueryKey({
        startTS: goalsTimelineList?.[currentReviewCycleIndex]?.startTS,
        endTS: goalsTimelineList?.[currentReviewCycleIndex]?.endTS,
        employeeId,
        formId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
      })
    );
  };

  const resetState = () => {
    // setGoals([]);
    setEditedGoalsWeight([]);
    setEmployeeDetails(null);
  };

  const handleOnClose = () => {
    resetState();
    hideSidePanel();
  };

  const handleOnWeightChange = ({ goalId, value }) => {
    const newWeight = value;
    if (newWeight < 0 || newWeight > 100 || Number.isNaN(newWeight)) {
      dispatch(showInfoSnackbar('Valid weightage range is 0-100'));
    } else {
      const editedGoalIndex = getGoalIndex({ goalId });
      const updatedEditedGoals = [...editedGoalsWeight];
      updatedEditedGoals[editedGoalIndex].weightage = newWeight;
      setEditedGoalsWeight(updatedEditedGoals);
    }
  };

  const handleDistributeWeight = () => {
    let weightageToDistribute = MAX_WEIGHTAGE - totalWeightage;
    if (weightageToDistribute <= 0 || editedGoalsWeight?.length < 1) {
      console.error('No goals are there to distribute weightage');
      return;
    }
    const newUpdatedEditedGoals = editedGoalsWeight.map((item) => item);

    while (weightageToDistribute > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < editedGoalsWeight.length; i++) {
        if (weightageToDistribute < 1) {
          // eslint-disable-next-line operator-assignment
          newUpdatedEditedGoals[i].weightage = newUpdatedEditedGoals[i].weightage + weightageToDistribute;
          weightageToDistribute = 0;
        } else {
          // eslint-disable-next-line no-plusplus
          newUpdatedEditedGoals[i].weightage++;
          // eslint-disable-next-line no-plusplus
          weightageToDistribute--;
        }
      }
    }
    setEditedGoalsWeight(
      newUpdatedEditedGoals.map((w) => ({
        ...w,
        weightage: roundOffToPlaces(w.weightage, 2),
      }))
    );
  };

  const saveUpdatedWeightage = () => {
    setIsSaving(true);
    const body = editedGoalsWeight.map((item) => ({
      goalId: item.uuid,
      weightage: item.weightage || 0,
    }));
    sendChangeWeightageEvent({
      eventProperties: {
        timeLineName: goalsTimelineList?.[currentReviewCycleIndex]?.name,
        empId: employeeId,
      },
    });
    updateReviewGoalsWeightage({
      employeeId,
      formId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
      body,
    })
      .then(() => {
        if (reviewId) {
          goalFormInvalidateHandler();
        }
        refetchCascadedViewData();
        hideSidePanel();
        // onSuccess && onSuccess({ editedGoals: editedGoalsWeight, employeeId });
      })
      .catch((err) => {
        console.error('Error while saving weightage', err);
        dispatch(showErrorSnackbar('Error while saving!'));
      })
      .finally(() => setIsSaving(false));
  };

  const fetchGoalsTimelineList = useCallback(async () => {
    setIsSaving(true);
    try {
      const employeeDetailsRes = await getEmployeeDetails(employeeId);
      setEmployeeDetails(employeeDetailsRes);
      const goalsTimelineListRes = await getReviewGoalsTimelineList({ employeeId });
      setGoalsTimelineList(goalsTimelineListRes);
      if (isPreSelectActiveGoals || preSelectedOptionId) {
        let activeGoalsTimelineIndex = 0;
        if (preSelectedOptionId) {
          activeGoalsTimelineIndex = goalsTimelineListRes.findIndex(
            ({ feedbackFormId }) => String(feedbackFormId) === preSelectedOptionId
          );
        } else if (isPreSelectActiveGoals) {
          activeGoalsTimelineIndex = goalsTimelineListRes.length - 1;
        }
        if (activeGoalsTimelineIndex !== -1) {
          setCurrentReviewCycleIndex(activeGoalsTimelineIndex);
        } else {
          setCurrentReviewCycleIndex(0);
        }
      } else {
        setCurrentReviewCycleIndex(0);
      }
    } catch (error) {
      if (error.status === 403) {
        dispatch(showErrorSnackbar('No Access'));
      } else {
        dispatch(showErrorSnackbar(error.message || 'Something went wrong. Please try again.'));
      }
    } finally {
      setIsSaving(false);
    }
  }, [employeeId, dispatch]);

  useEffect(() => {
    if (employeeId) {
      fetchGoalsTimelineList();
    }
  }, [employeeId, fetchGoalsTimelineList]);

  useEffect(() => {
    if (!open) return;
    if (goals) {
      setEditedGoalsWeight(goals);
    }
  }, [currentReviewCycleIndex, open, goals]);

  if (!open) {
    return null;
  }

  return (
    <Drawer
      isOpen={open}
      onClose={handleOnClose}
      header={
        <Header
          employeeDetails={employeeDetails}
          goalsTimelineList={goalsTimelineList}
          currentReviewCycleIndex={currentReviewCycleIndex}
          setCurrentReviewCycleIndex={setCurrentReviewCycleIndex}
        />
      }
      footer={
        <Box w="100%">
          <ProgressStatus theme={theme} totalWeightage={totalWeightage} />

          <Flex justify="flex-end" alignItems="center" mt={6}>
            <Button
              variant="tertiary"
              isDisabled={!canEdit || totalWeightage >= MAX_WEIGHTAGE || !currentSelectedOptionAllowEdit}
              mr={6}
              onClick={handleDistributeWeight}
            >
              Distribute Remaining
            </Button>
            <Button variant="outlined" w="80px" onClick={handleOnClose}>
              Cancel
            </Button>
            <Button
              ml="6"
              w="80px"
              isDisabled={!canEdit || totalWeightage !== MAX_WEIGHTAGE || !currentSelectedOptionAllowEdit || isSaving}
              onClick={saveUpdatedWeightage}
            >
              Save
            </Button>
          </Flex>
        </Box>
      }
    >
      <Box px="4">
        {(isLoading || isSaving) && <SkeletonLoader skeletons={['100%', '100%', '100%']} />}

        {!(isLoading || isSaving) && currentReviewCycleIndex !== null ? (
          <Box>
            {goals?.length > 0 ? (
              <Flex flexDir="column">
                {goals.map((goal) => {
                  const editedGoal = editedGoalsWeight?.filter((item) => item.uuid === goal.uuid)[0];
                  const goalWeightage = editedGoal?.weightage;

                  const isStrikedThroughStatus =
                    (goal?.goalClosureStatus === 'DROPPED' ||
                      goal?.goalClosureStatus === 'ACHIEVED' ||
                      goal?.goalClosureStatus === 'NOT_ACHIEVED') &&
                    goal?.status === 'COMPLETED';

                  return (
                    <React.Fragment key={goal.uuid}>
                      <Flex key={goal.uuid} alignItems="center" my="16px">
                        <Flex flexDir="column" flexGrow="1" p="0 6px">
                          <Flex alignItems="center">
                            {renderParentIcon(goal?.parentGoalBasicDetails?.goalType)}
                            <TextLabel
                              color="text.g2"
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '350px',
                              }}
                            >
                              {goal?.parentGoalBasicDetails?.goalTitle ?? 'No parent'}
                            </TextLabel>
                          </Flex>
                          <Flex alignItems="center">
                            {renderIcon({
                              type: goal?.type,
                              visibility: goal?.visibilityType,
                            })}
                            <TextParseComponent
                              color={goalWeightage > 0 ? theme.colors.text.g1 : theme.colors.text.g2}
                              text={goal.title}
                              isStrikedThrough={isStrikedThroughStatus}
                              isGreyedOut={goal?.goalClosureStatus === 'DROPPED'}
                            />
                          </Flex>
                        </Flex>
                        <Box w="25%" minW="25%">
                          <NumberInput
                            value={goalWeightage}
                            isDisabled={!(canEdit && currentSelectedOptionAllowEdit)}
                            onChange={(value) => {
                              handleOnWeightChange({ goalId: goal.uuid, value });
                            }}
                            precision={2}
                            borderColor="#0000003b"
                          >
                            <NumberInputField
                              borderRadius="4px"
                              _hover={{
                                borderColor: 'text.g1',
                              }}
                            />
                            <NumberInputStepper color="text.g2" justifyContent="center" fontSize="1rem">
                              %
                            </NumberInputStepper>
                          </NumberInput>
                        </Box>
                      </Flex>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </Flex>
            ) : (
              <Flex alignItems="center" justifyContent="center">
                <TextLabel>No Goals Found!</TextLabel>
              </Flex>
            )}
          </Box>
        ) : null}

        {!(isLoading || isSaving) && currentReviewCycleIndex === null ? (
          <StyledBox flexcontainer alignitems="center" justifycontent="center">
            <StyledTypography>No Goals Found!</StyledTypography>
          </StyledBox>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default GoalsWeightSidepanelV1;
